import {
  Card,
  CardContent,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import React, { useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import WebPromo from './promo/WebPromo'
import WebMobilePromo from './promo/WebMobilePromo'
import { usePromoModal } from '../context/PromoModalContext'
import useRudderStackAnalytics from '../useRudderAnalytics'
import { trackPopupShown, trackPopupDismissed } from '../utils/popupAnalytics'

interface PromoModalProps {
  onClose: (reason?: 'close_button' | 'backdrop' | 'escape') => void
  type: 'web' | 'mobile'
}

const PromoModal: React.FC<PromoModalProps> = ({ onClose, type }) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const params = useParams()
  const { lastTrigger, lastPage } = usePromoModal()
  const [analytics, isAnalyticsReady] = useRudderStackAnalytics()
  const hasTrackedShownRef = useRef(false)

  // Track SHOWN event once when modal opens
  useEffect(() => {
    if (!hasTrackedShownRef.current) {
      hasTrackedShownRef.current = true
      
      // Get restaurant alias from route params if available
      const restaurantAlias = params.alias ?? null
      
      trackPopupShown(
        {
          page: lastPage !== 'unknown' ? lastPage : undefined,
          trigger: lastTrigger !== 'unknown' ? lastTrigger : undefined,
          modalType: type,
          restaurantAlias,
        },
        analytics,
        isAnalyticsReady,
      )
    }
  }, [lastTrigger, lastPage, type, analytics, isAnalyticsReady, params.alias])

  // Reset tracking ref when modal closes (for next open)
  useEffect(() => {
    return () => {
      hasTrackedShownRef.current = false
    }
  }, [])

  const handleClose = useCallback((reason: 'close_button' | 'backdrop' | 'escape' = 'close_button'): void => {
    // Track dismissed event before closing
    const restaurantAlias = params.alias ?? null
    trackPopupDismissed(
      {
        page: lastPage !== 'unknown' ? lastPage : undefined,
        trigger: lastTrigger !== 'unknown' ? lastTrigger : undefined,
        modalType: type,
        dismissReason: reason,
        restaurantAlias,
      },
      analytics,
      isAnalyticsReady,
    )
    
    onClose(reason)
  }, [lastTrigger, lastPage, type, analytics, isAnalyticsReady, params.alias, onClose])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        handleClose('escape')
      }
    }
    
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [handleClose])

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <Box
        onClick={(e) => {
          // Close on backdrop click
          if (e.target === e.currentTarget) {
            handleClose('backdrop')
          }
        }}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 101,
        }}
      />
      <Card
        sx={{
          maxWidth: isSmallScreen ? 'calc(100% - 40px)' : 480,
          width: '100%',
          position: 'relative',
          borderRadius: '28px',
          background: theme.palette.primary[50],
          boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
          zIndex: 102,
        }}
      >
        <Box
          component="img"
          src="/new/images/promo-Vector-1.png"
          alt="promo card shadow"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            overflow: 'visible',
            width: '207px',
          }}
        ></Box>
        <Box
          component="img"
          src="/new/images/promo-Vector-2.png"
          alt="promo card shadow"
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            overflow: 'visible',
            width: '207px',
          }}
        ></Box>
        <IconButton
          onClick={() => handleClose('close_button')}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: '#1C1B1F',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <Close />
        </IconButton>

        <CardContent
          sx={{
            flex: 1,
            pb: '0 !important',
            px: type === 'web' ? { xs: 2, sm: 6 } : 0,
            pt: type === 'web' ? { xs: 7, sm: 10 } : 7,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {type === 'web' ? <WebPromo /> : <WebMobilePromo />}
        </CardContent>
      </Card>
    </Box>
  )
}

export default PromoModal
